/* eslint-disable react/jsx-props-no-spreading */
import { useUser } from '@folklore/auth';
import { useForm } from '@folklore/forms';
import { useUrlGenerator } from '@folklore/routes';
import { faCheck, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { camelCase } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import useSetUser from '../../hooks/useSetUser';
import * as AppPropTypes from '../../lib/PropTypes';

import StyledButton from '../buttons/StyledButton';
import CheckboxField from '../fields/Checkbox';
import EmailField from '../fields/Email';
import Body from '../partials/Body';

import styles from '../../../styles/forms/newsletter-form.module.scss';

const propTypes = {
    name: PropTypes.string,
    optin: PropTypes.string,
    placeholder: AppPropTypes.message,
    className: PropTypes.string,
    inputClassName: PropTypes.string,
    submitClassName: PropTypes.string,
    onSuccess: PropTypes.func,
};

const defaultProps = {
    name: null,
    optin: null,
    placeholder: null,
    className: null,
    inputClassName: null,
    submitClassName: null,
    onSuccess: null,
};

function NewsletterForm({
    name,
    placeholder,
    optin,
    className,
    inputClassName,
    submitClassName,
    onSuccess,
}) {
    const route = useUrlGenerator();
    const action = route('profile.newsletter');
    const user = useUser();
    const { email = null, [camelCase(`newsletter_${name}`)]: newsletterEmail = null } = user || {};
    const hasEmail = email !== null;
    const setUser = useSetUser();

    const onComplete = useCallback(
        (newUser) => {
            setUser(newUser);
            if (onSuccess !== null) {
                onSuccess(newUser);
            }
        },
        [setUser, onSuccess],
    );

    const initialValue = useMemo(
        () => ({
            name,
            email: newsletterEmail || email,
        }),
        [name, newsletterEmail, email],
    );

    const {
        fields,
        onSubmit,
        errors,
        loading,
        generalError = null,
    } = useForm({
        initialValue,
        fields: ['email', 'optin'],
        action,
        onComplete,
    });

    const { email: emailErrors = null, optin: optinErrors = null } = errors || {};
    const intl = useIntl();

    return (
        <form
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                    [styles.loading]: loading,
                    [styles.hasEmail]: hasEmail,
                },
            ])}
            onSubmit={onSubmit}
            action={action}
        >
            {optin !== null ? (
                <CheckboxField
                    {...fields.optin}
                    className={styles.optin}
                    required
                >
                    <Body body={optin} tag="span" />
                </CheckboxField>
            ) : null}
            <EmailField
                className={classNames([
                    styles.input,
                    { [inputClassName]: inputClassName !== null },
                ])}
                {...fields.email}
                placeholder={
                    placeholder !== null
                        ? placeholder
                        : intl.formatMessage({
                              defaultMessage: 'Email',
                              description: 'Field placeholder',
                          })
                }
                // required
            />
            {emailErrors !== null || generalError !== null || optinErrors !== null ? (
                <div className={styles.errors}>
                    {(emailErrors || []).map((error, errorIndex) => (
                        <div key={`error-${errorIndex + 1}`}>{error}</div>
                    ))}
                    {(optinErrors || []).map((error, errorIndex) => (
                        <div key={`option-error-${errorIndex + 1}`}>{error}</div>
                    ))}
                    {generalError !== null ? <div>{generalError}</div> : null}
                </div>
            ) : null}
            <StyledButton
                className={classNames([
                    styles.submit,
                    { [submitClassName]: submitClassName !== null },
                ])}
                colored
                type="submit"
            >
                <FormattedMessage defaultMessage="Subscribe" description="Button label" />
            </StyledButton>
        </form>
    );
}

NewsletterForm.propTypes = propTypes;
NewsletterForm.defaultProps = defaultProps;

export default NewsletterForm;
